import { useCallback } from "react";

import { addYears } from "date-fns";

import { intervals } from "@/components/pages_logged_in/certifications/types";
import { CalendarIcon } from "@/components/pages_logged_in/program/components/set-components/configure-inputs/CalendarIcon";
import { DateTimeInputV2 } from "@/components/pieces/DateTimeInputV2";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ExpirationInterval, ExpirationType } from "@/graphql";

export type ExpirationState = {
  type: ExpirationType;
  relativeExpirationValue: number | null;
  relativeExpirationInterval: ExpirationInterval;
  absoluteExpiration: string | null;
};

const NumberSelect = ({
  end,
  value,
  onChange,
}: {
  end: number;
  value: number | null;
  onChange: (value: number) => void;
}) => {
  return (
    <Select
      onValueChange={(value) => onChange(parseInt(value))}
      value={value?.toString()}
    >
      <SelectTrigger>
        <SelectValue placeholder="..." />
      </SelectTrigger>
      <SelectContent>
        {Array.from({ length: end }, (_, i) => (
          <SelectItem key={i} value={`${i + 1}`}>
            {i + 1}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
};

const IntervalSelect = ({
  plural,
  value,
  onChange,
}: {
  plural: boolean;
  value: ExpirationInterval;
  onChange: (value: ExpirationInterval) => void;
}) => {
  return (
    <Select onValueChange={onChange} value={value}>
      <SelectTrigger>
        <SelectValue placeholder="..." />
      </SelectTrigger>
      <SelectContent>
        {Object.entries(intervals).map(([key, value]) => (
          <SelectItem key={key} value={key}>
            {plural ? value : key.toLowerCase()}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
};

export function CertificationExpirationForm({
  expiration,
  onExpirationChange,
}: {
  expiration: ExpirationState;
  onExpirationChange: (expiration: ExpirationState) => void;
}) {
  const handleTypeChange = useCallback(
    (value: ExpirationType) => {
      onExpirationChange({ ...expiration, type: value });
    },
    [onExpirationChange, expiration],
  );
  const handleRelativeExpirationValueChange = useCallback(
    (value: number) => {
      onExpirationChange({ ...expiration, relativeExpirationValue: value });
    },
    [onExpirationChange, expiration],
  );
  const handleRelativeExpirationIntervalChange = useCallback(
    (value: ExpirationInterval) => {
      onExpirationChange({ ...expiration, relativeExpirationInterval: value });
    },
    [onExpirationChange, expiration],
  );
  const handleAbsoluteExpirationChange = useCallback(
    (value: string | null) => {
      onExpirationChange({ ...expiration, absoluteExpiration: value });
    },
    [onExpirationChange, expiration],
  );
  return (
    <RadioGroup value={expiration.type} onValueChange={handleTypeChange}>
      <div className="flex gap-2">
        <RadioGroupItem
          value={ExpirationType.NoExpiration}
          id="no-expiration"
        />
        <Label className="font-normal" htmlFor="no-expiration">
          This certification does not expire
        </Label>
      </div>
      <div className="flex gap-2  items-start sm:items-center">
        <div className="flex h-full sm:h-auto">
          <RadioGroupItem value={ExpirationType.Relative} id="relative" />
        </div>
        <Label
          className="font-normal flex sm:flex-row flex-col items-start sm:items-center pt-0.5 sm:pt-0"
          htmlFor="relative"
        >
          <div>Expires </div>
          <div className="flex gap-2">
            <NumberSelect
              end={
                expiration.relativeExpirationInterval === ExpirationInterval.Day
                  ? 50
                  : expiration.relativeExpirationInterval ===
                      ExpirationInterval.Week
                    ? 52
                    : expiration.relativeExpirationInterval ===
                        ExpirationInterval.Month
                      ? 36
                      : 10
              }
              value={expiration.relativeExpirationValue}
              onChange={handleRelativeExpirationValueChange}
            />{" "}
            <IntervalSelect
              plural={expiration.relativeExpirationValue !== 1}
              value={expiration.relativeExpirationInterval}
              onChange={handleRelativeExpirationIntervalChange}
            />
          </div>
          <div className="whitespace-nowrap">after awarded date</div>
        </Label>
      </div>
      <div className="flex gap-2 items-start sm:items-center">
        <div className="flex h-full sm:h-auto">
          <RadioGroupItem value={ExpirationType.Absolute} id="absolute" />
        </div>
        <Label
          className="font-normal flex sm:flex-row flex-col items-start sm:items-center pt-0.5 sm:pt-0"
          htmlFor="absolute"
        >
          Expires on{" "}
          <div className="relative">
            <CalendarIcon />
            <DateTimeInputV2
              minDateTime={new Date()}
              maxDateTime={addYears(new Date(), 10)}
              value={expiration.absoluteExpiration}
              onChange={handleAbsoluteExpirationChange}
            />
          </div>
        </Label>
      </div>
    </RadioGroup>
  );
}
